"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Loader2, type LucideIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/util/shadcn";

/* ──────────────────────────────────────────── */
/* 1 ▸ Variants                                */
/* ──────────────────────────────────────────── */

const base =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap" +
  " font-inter font-medium leading-none select-none" +
  " focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" +
  " disabled:pointer-events-none disabled:opacity-50 aria-busy:pointer-events-none ring-offset-background";

export const buttonVariants = cva(base, {
  variants: {
    variant: {
      primary: "bg-primary text-primary-foreground hover:bg-primary-hover",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
      outline: "border border-input bg-background text-foreground hover:bg-secondary-hover",
      ghost: "bg-transparent text-foreground hover:bg-muted",
      muted: "bg-muted text-muted-foreground hover:bg-muted-hover",
      link: "bg-transparent underline underline-offset-4 p-0 hover:no-underline",
      destructive: "bg-destructive text-destructive-foreground hover:bg-destructive-hover",
      "destructive-ghost": "bg-transparent text-destructive hover:bg-destructive-hover/15",
      success: "bg-success text-success-foreground hover:bg-success-hover",
    },
    size: {
      xs: "h-8 px-3 text-xs",
      sm: "h-9 px-3 text-sm",
      md: "h-10 px-4 text-sm",
      lg: "h-11 px-6 text-base",
      xl: "h-12 px-8 text-base",
    },
    shape: {
      rounded: "",
      square: "p-0 aspect-square gap-0",
      circle: "p-0 aspect-square gap-0",
    },
    radius: {
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      pill: "rounded-full",
    },
    block: {
      true: "w-full flex",
    },
  },
  compoundVariants: [
    { shape: "rounded", size: ["xs", "sm"], class: "gap-1.5" },
    { shape: "circle", class: "rounded-full" },
  ],
  defaultVariants: {
    variant: "primary",
    size: "md",
    shape: "rounded",
    radius: "md",
  },
});

export type ButtonVariants = VariantProps<typeof buttonVariants>;

/* ──────────────────────────────────────────── */
/* 2 ▸ Props                                   */
/* ──────────────────────────────────────────── */

export interface ButtonIcon {
  Icon: LucideIcon;
  position?: "left" | "right"; // default: left
}

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled">,
    ButtonVariants {
  asChild?: boolean;
  disabled?: boolean;
  icon?: ButtonIcon;
  isLoading?: boolean;
  innerClassName?: string;
}

/* ──────────────────────────────────────────── */
/* 3 ▸ Component                               */
/* ──────────────────────────────────────────── */

export const Button = React.forwardRef<HTMLElement, ButtonProps>(
  (
    {
      asChild = false,
      variant,
      size,
      shape,
      radius,
      block,
      icon,
      isLoading = false,
      disabled,
      children,
      className,
      innerClassName,
      ...props
    },
    ref
  ) => {
    const iconSize = { xs: 12, sm: 14, md: 16, lg: 18, xl: 20 }[size ?? "md"];

    const isDisabled = disabled || isLoading;
    const isIconOnly = shape === "square" || shape === "circle";

    /* ——————————————————————————————————————————————— */
    /* 3a ▸ asChild -- clone the *single* child element */
    /* ——————————————————————————————————————————————— */
    if (asChild) {
      if (process.env.NODE_ENV !== "production" && !React.isValidElement(children)) {
        throw new Error(
          "`<Button asChild>` expects exactly **one** React element (e.g. `<Link>`)."
        );
      }

      type AsChildProps = React.HTMLAttributes<HTMLElement> & {
        disabled?: boolean;
        ref?: React.Ref<HTMLElement>;
      };
      const child = children as React.ReactElement<AsChildProps>;

      return React.cloneElement<AsChildProps>(child, {
        ref,
        className: cn(
          buttonVariants({ variant, size, shape, radius, block }),
          className,
          child.props.className
        ),
        "aria-busy": isLoading || undefined,
        ...(!child.props.disabled &&
          !child.props["aria-disabled"] && {
            ...(child.type === "button"
              ? { disabled: isDisabled }
              : { "aria-disabled": isDisabled }),
          }),
        ...props,
      });
    }

    /* ——————————————————————————————————————————————— */
    /* 3b ▸ Normal <button> path                        */
    /* ——————————————————————————————————————————————— */

    const iconNode = icon && (
      <icon.Icon size={iconSize} aria-hidden className={isIconOnly ? "" : "shrink-0"} />
    );

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        disabled={isDisabled}
        aria-busy={isLoading || undefined}
        className={cn(
          "relative",
          buttonVariants({ variant, size, shape, radius, block }),
          className
        )}
        {...props}
      >
        {/* Spinner overlay */}
        {isLoading && (
          <Loader2 size={iconSize} className="absolute inset-0 m-auto animate-spin" aria-hidden />
        )}

        {/* Content wrapper (hidden while loading) */}
        <span
          className={
            innerClassName ??
            cn(
              isLoading && "opacity-0",
              "inline-flex items-center gap-2",
              isIconOnly && "w-full justify-center",
              icon?.position === "right" && "flex-row-reverse"
            )
          }
        >
          {iconNode}
          {!isIconOnly && children}
        </span>
      </button>
    );
  }
);

Button.displayName = "Button";
